import { useCallback, useEffect, useMemo, useState } from "react";
import { DatePicker, Drawer, Modal, Spin, Tag, Tooltip } from "antd";
import Icon from "components/common/Icon";
import { ADMIN_COLORS as C } from "components/ovizia/monitoring/adminColors";
import useInboundFaxReviews from "hooks/care/useInboundFaxReviews";
import {
  fetchInboundFaxDocument,
  fetchReviewResidentDetail,
  searchReviewResidents,
} from "hooks/care/inboundFaxReviewApi";
import { toast } from "react-toastify";

const MATCH_LABEL = {
  EXACT: { label: "Exact match", tone: "positive" },
  UNIQUE: { label: "Exact match", tone: "positive" },
  AMBIGUOUS: { label: "Ambiguous", tone: "warning" },
  MULTIPLE: { label: "Ambiguous", tone: "warning" },
  NONE: { label: "No match", tone: "negative" },
};

const TONE_STYLE = {
  positive: { bg: C.greenBg, fg: C.green },
  warning: { bg: C.amberBg, fg: C.amber },
  negative: { bg: C.redBg, fg: C.red },
  neutral: { bg: C.blueBg, fg: C.blue },
};

const FILTERS = [
  { value: "all", label: "All" },
  { value: "matched", label: "Matched" },
  { value: "needsResident", label: "Needs a resident" },
];

const RESIDENT_SEARCH_MIN_NAME = 3;

const PREVIEW_MAX_HEIGHT = "58vh";

function Pill({ tone, children }) {
  const style = TONE_STYLE[tone] || TONE_STYLE.neutral;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        background: style.bg,
        color: style.fg,
        fontSize: 11,
        fontWeight: 700,
        padding: "3px 9px",
        borderRadius: 999,
        width: "max-content",
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </span>
  );
}

function ActionButton({ tone, disabled, busy, icon, children, ...rest }) {
  const style = TONE_STYLE[tone] || TONE_STYLE.neutral;
  return (
    <button
      {...rest}
      disabled={disabled || busy}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        fontSize: 12,
        fontWeight: 700,
        color: disabled || busy ? C.textFaint : style.fg,
        background: disabled || busy ? C.bgSurface : style.bg,
        border: `1px solid ${disabled || busy ? C.border : style.fg}`,
        borderRadius: 7,
        padding: "7px 12px",
        cursor: disabled || busy ? "not-allowed" : "pointer",
      }}
    >
      {busy ? <Icon name="refresh" size={13} /> : <Icon name={icon} size={13} />}
      {children}
    </button>
  );
}

/** Workspace ids are long; showing the first group keeps them comparable at a glance. */
function shortId(value) {
  return value ? String(value).slice(0, 8) : null;
}

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})/;

function formatDate(value) {
  const match = ISO_DATE.exec(String(value ?? "").trim());
  return match ? `${match[2]}/${match[3]}/${match[1]}` : value || "";
}

/** Same order as {@link formatDate}, plus the time of day for queue timestamps. */
function formatDateTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const time = date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  return `${month}/${day}/${date.getFullYear()} ${time}`;
}


function ageFrom(birthDate) {
  const match = ISO_DATE.exec(String(birthDate ?? "").trim());
  if (!match) return null;
  const born = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  if (Number.isNaN(born.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - born.getFullYear();
  const monthDiff = now.getMonth() - born.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < born.getDate())) age -= 1;
  return age >= 0 && age < 130 ? age : null;
}


function initials(resident) {
  const parts = [resident.firstName, resident.lastName]
    .map((part) => String(part || "").trim())
    .filter(Boolean);
  if (parts.length === 0) return "?";
  return parts.map((part) => part[0].toUpperCase()).slice(0, 2).join("");
}


function residentLocation(resident) {
  const place = [resident.roomNumber && `Room ${resident.roomNumber}`, resident.unit, resident.wing]
    .filter(Boolean)
    .join(" · ");
  return place || null;
}

function residentFact(label, value) {
  return value ? { label, value } : null;
}

function dobMatchesFax(review, resident) {
  const faxDob = (review.patientDateOfBirth || "").trim();
  return Boolean(faxDob) && Boolean(resident.birthDate) && faxDob === resident.birthDate;
}


function ResidentIdentity({ resident, tone = "neutral", footer }) {
  const dob = formatDate(resident.birthDate);
  const age = ageFrom(resident.birthDate);
  const facts = [
    residentFact("DOB", [dob, age != null && `${age}y`].filter(Boolean).join(" · ")),
    residentFact("", residentLocation(resident)),
    residentFact("Status", resident.status),
    residentFact("Record", resident.medicalRecordNumber),
  ].filter(Boolean);

  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 9, minWidth: 0 }}>
      <div
        style={{
          flex: "0 0 auto",
          width: 30,
          height: 30,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: TONE_STYLE[tone]?.bg || C.blueBg,
          color: TONE_STYLE[tone]?.fg || C.blue,
          fontSize: 11,
          fontWeight: 800,
        }}
      >
        {initials(resident)}
      </div>
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontSize: 12.5,
            fontWeight: 700,
            color: C.text,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {residentName(resident)}
          {resident.preferredName && (
            <span style={{ fontWeight: 500, color: C.textMuted }}> “{resident.preferredName}”</span>
          )}
        </div>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "2px 10px",
            fontSize: 11,
            color: C.textMuted,
            marginTop: 1,
          }}
        >
          {facts.map((fact, index) => (
            <span key={`${fact.label}-${fact.value}-${index}`}>
              {fact.label && (
                <span style={{ fontWeight: 700, color: C.textFaint }}>{fact.label} </span>
              )}
              {fact.value}
            </span>
          ))}
        </div>
        {footer}
      </div>
    </div>
  );
}

function residentName(resident) {
  if (resident.fullName) return resident.fullName;
  return [resident.firstName, resident.middleName, resident.lastName]
    .filter(Boolean)
    .join(" ");
}


function DocumentPreview({ review }) {
  const [state, setState] = useState({ loading: false, doc: null, error: null });

  const load = useCallback(async () => {
    setState({ loading: true, doc: null, error: null });
    try {
      const doc = await fetchInboundFaxDocument(review.id);
      setState({ loading: false, doc, error: null });
    } catch (err) {
      setState({
        loading: false,
        doc: null,
        error: err?.message || "The fax document could not be loaded.",
      });
    }
  }, [review.id]);

  useEffect(() => {
    load();
  }, [load]);

  const isImage = (state.doc?.contentType || "").startsWith("image/");

  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 10,
          marginBottom: 8,
        }}
      >
        <span style={{ fontSize: 12, color: C.textMuted, wordBreak: "break-all" }}>
          {state.doc?.fileName || review.documentFileName || "No document name"}
        </span>
        <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
          <Tooltip title="Fetch a new link. Storage signs these for minutes only.">
            <ActionButton tone="neutral" icon="refresh" onClick={load} busy={state.loading}>
              Relink
            </ActionButton>
          </Tooltip>
          {state.doc && (
            <a
              href={state.doc.signedUrl}
              target="_blank"
              rel="noreferrer"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                fontSize: 12,
                fontWeight: 700,
                color: C.blue,
                background: C.blueBg,
                border: `1px solid ${C.blue}`,
                borderRadius: 7,
                padding: "7px 12px",
                textDecoration: "none",
              }}
            >
              <Icon name="external-link" size={13} /> Open
            </a>
          )}
        </div>
      </div>

      {state.loading && (
        <div style={{ padding: 30, textAlign: "center" }}>
          <Spin size="small" />
        </div>
      )}

      {!state.loading && state.error && (
        <div
          style={{
            fontSize: 12,
            color: C.red,
            background: C.redBg,
            border: `1px solid ${C.red}`,
            borderRadius: 7,
            padding: "10px 12px",
          }}
        >
          {state.error}
        </div>
      )}

      {!state.loading && state.doc && (
        <div
          style={{
            border: `1px solid ${C.border}`,
            borderRadius: 8,
            background: C.bgSurface,
            overflow: "hidden",
            maxHeight: PREVIEW_MAX_HEIGHT,
          }}
        >
          {isImage ? (
            <img
              src={state.doc.signedUrl}
              alt={state.doc.fileName || "Fax document"}
              onError={() => {
                load();
              }}
              style={{
                width: "100%",
                display: "block",
                objectFit: "contain",
                maxHeight: PREVIEW_MAX_HEIGHT,
              }}
            />
          ) : (
            <iframe
              key={state.doc.signedUrl}
              title={state.doc.fileName || "Fax document"}
              src={state.doc.signedUrl}
              style={{
                width: "100%",
                height: PREVIEW_MAX_HEIGHT,
                maxHeight: "100%",
                border: "none",
                display: "block",
              }}
            />
          )}
        </div>
      )}
    </div>
  );
}

function ResidentSearch({ review, onPicked, busy }) {
  const [name, setName] = useState("");
  const [birthDate, setBirthDate] = useState(null);
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState(null);

  const nameReady = name.trim().length >= RESIDENT_SEARCH_MIN_NAME;
  const dateReady = Boolean(birthDate);
  const canSearch = nameReady && dateReady;

  const runSearch = async () => {
    if (!canSearch) return;
    setSearching(true);
    setError(null);
    setSearched(true);
    try {
      const found = await searchReviewResidents(review.id, {
        name: name.trim(),
        birthDate: birthDate.format("YYYY-MM-DD"),
      });
      setResults(found);
      if (found.length === 0) {
        setError("No resident matches that name and date of birth. Check the spelling, or search without the date.");
      }
    } catch (err) {
      setResults([]);
      setError(err?.message || "The resident search failed.");
    } finally {
      setSearching(false);
    }
  };

  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ display: "flex", gap: 8, alignItems: "flex-start", flexWrap: "wrap" }}>
        <div style={{ flex: "1 1 200px", minWidth: 0 }}>
          <label
            style={{
              display: "block",
              fontSize: 11,
              fontWeight: 700,
              color: C.textMuted,
              marginBottom: 4,
            }}
          >
            Resident name (required)
          </label>
          <input
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setSearched(false);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && canSearch) runSearch();
            }}
            placeholder="Name"
            style={{
              width: "100%",
              boxSizing: "border-box",
              fontSize: 13,
              fontFamily: "inherit",
              border: `1px solid ${nameReady ? C.border : C.amber}`,
              borderRadius: 7,
              padding: "8px 10px",
              outline: "none",
            }}
          />
        </div>

        <div style={{ flex: "0 0 auto" }}>
          <label
            style={{
              display: "block",
              fontSize: 11,
              fontWeight: 700,
              color: C.textMuted,
              marginBottom: 4,
            }}
          >
            Date of birth (required)
          </label>
          <DatePicker
            value={birthDate}
            onChange={(value) => {
              setBirthDate(value);
              setSearched(false);
            }}
            placeholder="MM/DD/YYYY"
            format="MM/DD/YYYY"
            style={{ fontSize: 13, width: "100%" }}
          />
        </div>

        <div style={{ flex: "0 0 auto", paddingTop: 18 }}>
          <ActionButton
            tone="primary"
            icon="search"
            disabled={!canSearch}
            busy={searching}
            onClick={runSearch}
          >
            Search
          </ActionButton>
        </div>
      </div>

      {!dateReady && (
        <div style={{ fontSize: 11, color: C.textMuted, marginTop: 6 }}>
          Pick a date of birth as well. It is what separates residents who share a name.
        </div>
      )}

      {searched && (
        <div style={{ marginTop: 10 }}>
          {results.length === 0 && (
            <div style={{ fontSize: 11.5, color: C.textMuted, padding: "4px 2px" }}>
              No resident matches that name.
            </div>
          )}
          {results.map((resident) => (
            <div
              key={resident.residentId}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 10,
                border: `1px solid ${resident.birthDateMatchesFax ? C.green : C.border}`,
                borderRadius: 7,
                padding: "8px 10px",
                marginBottom: 5,
                background: resident.birthDateMatchesFax ? C.greenBg : "#fff",
              }}
            >
              <div style={{ minWidth: 0 }}>
                <ResidentIdentity
                  resident={resident}
                  tone={resident.birthDateMatchesFax ? "positive" : "neutral"}
                />
                {resident.birthDateMatchesFax && (
                  <div style={{ fontSize: 10.5, color: C.green, fontWeight: 700, marginTop: 3 }}>
                    Date of birth matches the fax
                  </div>
                )}
              </div>
              <ActionButton
                tone="positive"
                icon="check"
                disabled={busy}
                onClick={() => onPicked(resident)}
              >
                Use this resident
              </ActionButton>
            </div>
          ))}
        </div>
      )}

      {error && (
        <div
          style={{
            fontSize: 11,
            color: C.amber,
            background: C.amberBg,
            border: `1px solid ${C.amber}`,
            borderRadius: 6,
            padding: "7px 9px",
            marginTop: 8,
          }}
        >
          {error}
        </div>
      )}
    </div>
  );
}

function ResidentDetail({ review, residentId }) {
  const [state, setState] = useState({ loading: true, resident: null, error: null });

  useEffect(() => {
    let cancelled = false;
    setState({ loading: true, resident: null, error: null });
    fetchReviewResidentDetail(review.id, residentId)
      .then((resident) => {
        if (!cancelled) setState({ loading: false, resident, error: null });
      })
      .catch((err) => {
        if (!cancelled) {
          setState({
            loading: false,
            resident: null,
            error: err?.message || "Resident details could not be loaded.",
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [review.id, residentId]);

  if (state.loading) {
    return (
      <div style={{ padding: 20, textAlign: "center" }}>
        <Spin size="small" />
      </div>
    );
  }

  if (state.error) {
    return (
      <div style={{ fontSize: 12, color: C.red, background: C.redBg, padding: "10px 12px", borderRadius: 7 }}>
        {state.error}
      </div>
    );
  }

  const resident = state.resident;
  const faxDob = (review.patientDateOfBirth || "").trim();
  const dobMatches =
    Boolean(faxDob) && Boolean(resident.birthDate) && faxDob === resident.birthDate;
  const dobDiffers =
    Boolean(faxDob) && Boolean(resident.birthDate) && faxDob !== resident.birthDate;

  const rows = [
    ["Gender", resident.gender],
    ["Primary language", resident.primaryLanguage],
    ["Room", resident.roomNumber],
    ["Unit", resident.unit],
    ["Wing", resident.wing],
    ["Move-in date", formatDate(resident.moveInDate)],
    ["Workspace", resident.workspaceId],
  ].filter(([, value]) => value);

  return (
    <div>
      <div style={{ marginBottom: 12 }}>
        <ResidentIdentity
          resident={resident}
          tone={dobMatches ? "positive" : dobDiffers ? "negative" : "neutral"}
        />
      </div>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
        {dobMatches && <Tag color="green">Date of birth matches the fax</Tag>}
        {dobDiffers && <Tag color="red">Date of birth differs from the fax</Tag>}
        {!faxDob && <Tag>No date of birth was read from the fax</Tag>}
      </div>

      <dl style={{ margin: 0, fontSize: 12 }}>
        {rows.map(([label, value]) => (
          <div
            key={label}
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 14,
              padding: "6px 0",
              borderTop: `1px solid ${C.border}`,
            }}
          >
            <dt style={{ color: C.textMuted, fontWeight: 600 }}>{label}</dt>
            <dd style={{ margin: 0, color: C.text, textAlign: "right" }}>{String(value)}</dd>
          </div>
        ))}
      </dl>

      <div style={{ marginTop: 14 }}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: 0.4,
            color: C.textMuted,
            marginBottom: 6,
          }}
        >
          Medications on the fax ({review.medications.length})
        </div>
        {review.medications.length === 0 ? (
          <div style={{ fontSize: 12, color: C.textMuted }}>
            No medications were read from this fax.
          </div>
        ) : (
          <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
            {review.medications.map((medication, index) => (
              <li
                key={`${medication.medicationName}-${index}`}
                style={{
                  fontSize: 12,
                  color: C.text,
                  padding: "6px 0",
                  borderTop: `1px solid ${C.border}`,
                }}
              >
                <strong>{medication.medicationName}</strong>
                <div style={{ color: C.textMuted, fontSize: 11 }}>
                  {[
                    medication.genericName,
                    medication.strength,
                    [medication.dosage, medication.doseUnit].filter(Boolean).join(" "),
                    medication.frequency,
                    medication.route,
                    medication.prescribedBy,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function ReviewCard({ review, pendingId, onConfirm, onReject, onAddCandidate }) {
  const [selectedId, setSelectedId] = useState(review.suggestedResidentId || "");
  const [searchedResident, setSearchedResident] = useState(null);
  const [notes, setNotes] = useState("");
  const [detailFor, setDetailFor] = useState(null);

  const match = MATCH_LABEL[review.matchType] || { label: review.matchType, tone: "neutral" };
  const busy = pendingId === review.id;

  const selected =
    review.candidates.find((c) => c.residentId === selectedId) || searchedResident;
  const selectedResidentId = selected?.residentId || selected?.id || null;

  const handlePicked = async (resident) => {
    try {
      await onAddCandidate(review.id, resident.residentId);
      setSearchedResident(resident);
      setSelectedId("");
    } catch {
    }
  };

  return (
    <div
      style={{
        border: `1px solid ${C.border}`,
        borderRadius: 10,
        background: "#fff",
        padding: "16px 18px",
        marginBottom: 12,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 14,
          flexWrap: "wrap",
          alignItems: "flex-start",
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            <Pill tone={match.tone}>
              <Icon name="user-search" size={12} /> {match.label}
            </Pill>
            <span style={{ fontSize: 11, color: C.textFaint }}>
              {formatDateTime(review.createdAt)}
            </span>
          </div>

          <div style={{ fontSize: 15, fontWeight: 700, color: C.text, marginTop: 8 }}>
            {review.patientName || "Patient name not read"}
          </div>
          <div style={{ fontSize: 12, color: C.textMuted }}>
            {[
              review.patientDateOfBirth && `DOB ${formatDate(review.patientDateOfBirth)}`,
              review.documentFileName,
            ]
              .filter(Boolean)
              .join(" · ") || "No document name"}
          </div>
        </div>

        <div style={{ display: "flex", gap: 8 }}>
          <ActionButton tone="neutral" icon="eye" onClick={() => setDetailFor("document")}>
            Preview
          </ActionButton>
          <ActionButton
            tone="negative"
            icon="trash"
            busy={busy}
            onClick={() => onReject(review, notes)}
          >
            Reject
          </ActionButton>
          <ActionButton
            tone="positive"
            icon="check"
            busy={busy}
            disabled={!selectedResidentId}
            onClick={() => onConfirm(review, selectedResidentId, notes)}
          >
            Confirm
          </ActionButton>
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1.1fr) minmax(0, 1fr)",
          gap: 20,
          marginTop: 16,
        }}
      >
        <div>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: 0.4,
              color: C.textMuted,
              marginBottom: 8,
            }}
          >
            Resident ({review.candidateCount})
          </div>

          {review.candidates.length === 0 ? (
            <div style={{ fontSize: 12, color: C.textMuted }}>
              This fax matched nobody, so search for the resident it belongs to.
              <ResidentSearch
                review={review}
                busy={busy}
                onPicked={handlePicked}
              />
            </div>
          ) : (
            <div>
              {review.candidates.map((candidate) => (
                <div
                  key={candidate.residentId}
                  onClick={() => {
                    setSelectedId(candidate.residentId);
                    setSearchedResident(null);
                  }}
                  style={{
                    display: "flex",
                    gap: 9,
                    alignItems: "flex-start",
                    fontSize: 12,
                    color: C.text,
                    border: `1px solid ${
                      selectedId === candidate.residentId ? C.blue : C.border
                    }`,
                    background:
                      selectedId === candidate.residentId ? C.blueBg : "#fff",
                    borderRadius: 7,
                    padding: "8px 10px",
                    marginBottom: 5,
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="radio"
                    name={`resident-${review.id}`}
                    aria-label={`Select ${residentName(candidate)}`}
                    checked={selectedId === candidate.residentId}
                    onChange={() => {
                      setSelectedId(candidate.residentId);
                      setSearchedResident(null);
                    }}
                    style={{ marginTop: 2 }}
                  />
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <ResidentIdentity
                      resident={candidate}
                      tone={
                        selectedId === candidate.residentId
                          ? "neutral"
                          : dobMatchesFax(review, candidate)
                            ? "positive"
                            : "neutral"
                      }
                      footer={
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                            marginTop: 5,
                          }}
                        >
                          <button
                            type="button"
                            onClick={(event) => {
                              event.preventDefault();
                              event.stopPropagation();
                              setDetailFor(candidate.residentId);
                            }}
                            style={{
                              background: "none",
                              border: "none",
                              padding: 0,
                              color: C.blue,
                              fontSize: 11,
                              fontWeight: 700,
                              cursor: "pointer",
                            }}
                          >
                            View details
                          </button>
                          <span style={{ fontSize: 10.5, color: C.textFaint }}>
                            {[
                              candidate.medicalRecordNumber &&
                                `Record ${candidate.medicalRecordNumber}`,
                              shortId(candidate.workspaceId) &&
                                `Workspace ${shortId(candidate.workspaceId)}`,
                            ]
                              .filter(Boolean)
                              .join(" · ")}
                          </span>
                        </div>
                      }
                    />
                  </div>
                </div>
              ))}

              {searchedResident && (
                <div
                  style={{
                    fontSize: 11,
                    color: C.green,
                    background: C.greenBg,
                    border: `1px solid ${C.green}`,
                    borderRadius: 6,
                    padding: "7px 9px",
                    marginTop: 6,
                  }}
                >
                  Added from search: {residentName(searchedResident)}
                  {residentLocation(searchedResident)
                    ? ` · ${residentLocation(searchedResident)}`
                    : ""}
                </div>
              )}
            </div>
          )}

          {review.matchNotes && (
            <div
              style={{
                fontSize: 11,
                color: C.textMuted,
                background: C.bgSurface,
                border: `1px solid ${C.border}`,
                borderRadius: 6,
                padding: "7px 9px",
                marginTop: 8,
              }}
            >
              {review.matchNotes}
            </div>
          )}
        </div>

        <div>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: 0.4,
              color: C.textMuted,
              marginBottom: 8,
            }}
          >
            Medications ({review.medications.length})
          </div>

          {review.medications.length === 0 ? (
            <div style={{ fontSize: 12, color: C.textMuted }}>
              No medications were read from this fax.
            </div>
          ) : (
            <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
              {review.medications.map((medication, index) => (
                <li
                  key={`${medication.medicationName}-${index}`}
                  style={{
                    fontSize: 12,
                    color: C.text,
                    padding: "6px 0",
                    borderTop: `1px solid ${C.border}`,
                  }}
                >
                  <strong>{medication.medicationName}</strong>
                  <div style={{ color: C.textMuted, fontSize: 11 }}>
                    {[
                      medication.genericName,
                      medication.strength,
                      [medication.dosage, medication.doseUnit].filter(Boolean).join(" "),
                      medication.frequency,
                      medication.route,
                      medication.prescribedBy,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </div>
                </li>
              ))}
            </ul>
          )}

          <input
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Review notes (optional)"
            style={{
              width: "100%",
              boxSizing: "border-box",
              marginTop: 14,
              fontSize: 12,
              fontFamily: "inherit",
              border: `1px solid ${C.border}`,
              borderRadius: 7,
              padding: "8px 10px",
              outline: "none",
            }}
          />
        </div>
      </div>

      <Drawer
        open={Boolean(detailFor)}
        onClose={() => setDetailFor(null)}
        width={detailFor === "document" ? 720 : 520}
        zIndex={1050}
        push={false}
        maskClosable
        title={
          detailFor === "document"
            ? "Fax document"
            : `Resident — ${residentName(
                review.candidates.find((c) => c.residentId === detailFor) || {},
              )}`
        }
        styles={{
          header: { flex: "0 0 auto", wordBreak: "break-word", whiteSpace: "normal" },
          body: { overflowX: "hidden" },
        }}
      >
        {detailFor === "document" ? (
          <DocumentPreview review={review} />
        ) : (
          <ResidentDetail review={review} residentId={detailFor} />
        )}
      </Drawer>
    </div>
  );
}

export default function InboundFaxReviewPage() {
  const { reviews, loading, error, pendingId, refresh, confirm, reject, addCandidate } =
    useInboundFaxReviews();
  const [filter, setFilter] = useState("all");

  const visible = useMemo(() => {
    if (filter === "matched") {
      return reviews.filter((r) => r.candidateCount === 1);
    }
    if (filter === "needsResident") {
      return reviews.filter((r) => r.candidateCount !== 1);
    }
    return reviews;
  }, [reviews, filter]);

  const needsResidentCount = reviews.filter((r) => r.candidateCount !== 1).length;


  const handleConfirm = (review, residentId, notes) => {
    const candidate =
      review.candidates.find((c) => c.residentId === residentId) || {};
    Modal.confirm({
      title: "Confirm this fax and create medication drafts?",
      okText: "Confirm and create drafts",
      cancelText: "Go back",
      zIndex: 1050,
      content: (
        <div style={{ fontSize: 13, lineHeight: 1.6 }}>
          <p style={{ margin: "8px 0" }}>
            This will create{" "}
            <strong>{review.medications.length}</strong> medication draft
            {review.medications.length === 1 ? "" : "s"} for:
          </p>
          <p style={{ margin: "4px 0", fontWeight: 700 }}>{residentName(candidate)}</p>
          <p style={{ margin: "4px 0", color: C.textMuted }}>
            {[residentLocation(candidate), candidate.status && `Status ${candidate.status}`]
              .filter(Boolean)
              .join(" · ")}
          </p>
          <p style={{ margin: "4px 0", color: C.textMuted }}>
            Fax read as {review.patientName || "no patient name"}
            {review.patientDateOfBirth
              ? ` (DOB ${formatDate(review.patientDateOfBirth)})`
              : ""}
            .
          </p>
          <p style={{ margin: "8px 0 0", color: C.textMuted }}>
            Drafts stay pending until care approves them. The fax will leave this queue.
          </p>
          {candidate.workspaceId && (
            <p style={{ margin: "4px 0 0", color: C.textMuted }}>
              Drafts will be created against workspace{" "}
              <span style={{ fontFamily: "monospace" }}>{candidate.workspaceId}</span>.
            </p>
          )}
        </div>
      ),
      onOk: async () => {
        try {
          await confirm(review.id, { residentId, notes });
          toast.success("Fax confirmed and medication drafts created");
        } catch {
        }
      },
    });
  };

  const handleReject = (review, notes) => {
    Modal.confirm({
      title: "Reject this fax?",
      okText: "Reject the fax",
      cancelText: "Go back",
      okButtonProps: { danger: true },
      zIndex: 1050,
      content: (
        <div style={{ fontSize: 13, lineHeight: 1.6 }}>
          <p style={{ margin: "8px 0" }}>
            Nothing will be routed and no medication drafts will be created. This cannot be
            undone from this screen.
          </p>
          {review.patientName && (
            <p style={{ margin: "4px 0", color: C.textMuted }}>
              Fax read as {review.patientName}
              {review.patientDateOfBirth
                ? ` (DOB ${formatDate(review.patientDateOfBirth)})`
                : ""}
              .
            </p>
          )}
        </div>
      ),
      onOk: async () => {
        try {
          await reject(review.id, { notes });
          toast.success("Fax rejected");
        } catch {
        }
      },
    });
  };

  return (
    <div
      style={{
        minHeight: "60vh",
        display: "flex",
        flexDirection: "column",
        padding: "24px 28px",
        fontFamily: "Inter, system-ui, sans-serif",
        boxSizing: "border-box",
        overflow: "visible",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          marginBottom: 18,
          flexShrink: 0,
          gap: 16,
        }}
      >
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 800, color: C.green, margin: 0 }}>
            Inbound fax review
          </h1>
          <p style={{ fontSize: 13, color: C.textMuted, margin: "4px 0 0" }}>
            Every inbound fax waits here before it is routed. Nothing reaches a resident,
            and no medication draft is created, until you confirm it.
          </p>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {needsResidentCount > 0 && (
            <Pill tone="warning">{needsResidentCount} need a resident</Pill>
          )}
          <ActionButton tone="neutral" icon="refresh" onClick={refresh}>
            Refresh
          </ActionButton>
        </div>
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 14, flexShrink: 0 }}>
        {FILTERS.map((option) => (
          <button
            key={option.value}
            onClick={() => setFilter(option.value)}
            style={{
              fontSize: 12,
              fontWeight: 700,
              color: filter === option.value ? "#fff" : C.textMuted,
              background: filter === option.value ? C.green : "#fff",
              border: `1px solid ${filter === option.value ? C.green : C.border}`,
              borderRadius: 999,
              padding: "6px 14px",
              cursor: "pointer",
            }}
          >
            {option.label}
          </button>
        ))}
      </div>

      {error && (
        <div
          style={{
            fontSize: 12,
            color: C.red,
            background: C.redBg,
            border: `1px solid ${C.red}`,
            borderRadius: 7,
            padding: "9px 12px",
            marginBottom: 12,
            flexShrink: 0,
          }}
        >
          {error}
        </div>
      )}

      <div style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: "center", color: C.textFaint, fontSize: 13 }}>
            Loading faxes…
          </div>
        ) : visible.length === 0 ? (
          <div style={{ padding: 40, textAlign: "center", color: C.textFaint, fontSize: 13 }}>
            {reviews.length === 0
              ? "No faxes are waiting for review."
              : "No faxes match this filter."}
          </div>
        ) : (
          visible.map((review) => (
            <ReviewCard
              key={review.id}
              review={review}
              pendingId={pendingId}
              onConfirm={handleConfirm}
              onReject={handleReject}
              onAddCandidate={addCandidate}
            />
          ))
        )}
      </div>
    </div>
  );
}
